import { useContext, useEffect, useRef } from 'react';
import { SidebarContext } from '../ui/sidebarContext';

/**
 * Collapse the app sidebar to its icon rail while `compact` is true, and put it
 * back as it was afterwards.
 *
 * The split view gives the conversation about a third of the screen, and a full
 * navigation column out of that third is most of the text width gone. A
 * dashboard marked compact_sidebar wants the same thing for the same reason.
 * Restoring matters as much as collapsing: someone who never asked for this
 * should not find their sidebar quietly changed once they leave.
 *
 * Three things this has to get right, each of which it got wrong before:
 *
 *   - It must not fight the user. The effect runs on the `compact` transition
 *     only. Depending on `setOpen` — whose identity changes with `open`, since
 *     it closes over it — meant every expand re-ran this and collapsed the
 *     sidebar straight back, so the rail could not be opened at all.
 *   - It must not persist. setOpen writes a 7-day path=/ cookie, so collapsing
 *     here left every other page in the app collapsed for a week. The transient
 *     setter moves the sidebar without touching that remembered preference.
 *   - It must put it back. Including when the component unmounts, which is what
 *     navigating away from the dashboard does.
 *
 * Reads the context directly rather than through useSidebar, which throws when
 * there is no provider: a chromeless dashboard has no sidebar to collapse, and
 * a caller there should get a no-op, not a crash.
 */
export function useCompactSidebar(compact: boolean) {
  const sidebar = useContext(SidebarContext);

  // Everything the effect needs, read through refs so none of it lands in the
  // dependency list. The effect's only trigger is `compact` changing.
  const sidebarRef = useRef(sidebar);
  sidebarRef.current = sidebar;
  const restoreTo = useRef<boolean | null>(null);

  useEffect(() => {
    const current = sidebarRef.current;
    // No sidebar on this page at all (chromeless), nothing to collapse. On
    // mobile it is a sheet that is already closed, and collapsing here would
    // fight the Sheet's own open state.
    if (!current || current.isMobile) return;

    if (!compact) return;
    if (!current.open) return;

    restoreTo.current = true;
    current.setOpenTransient(false);

    return () => {
      const latest = sidebarRef.current;
      if (restoreTo.current === null || !latest) return;
      // Left alone if the viewer re-expanded it themselves in the meantime —
      // that is their call, and putting it back would undo it.
      if (!latest.open) {
        latest.setOpenTransient(restoreTo.current);
      }
      restoreTo.current = null;
    };
  }, [compact]);
}
